import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<ShadowVolumeMask>`: the stencil half of an unfiltered shadowed 2D light. It draws the light's
 * shadow volumes with colour writes off, stamping a stencil value its cookie quad rejects through
 * `litQuadStencilProps(ordinal)`. A PCF5/PCF13 shadow is a fraction no stencil carries, so a filtered
 * light samples `shadowPolarMap` (ADR-0030) over the whole rect and uses only the render-order helpers.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { buildShadowVolumes, } from './shadowVolumes';
import { intersectScissor, windowScissor } from './lightListWindow';
import { canvasItemFacing } from '../canvasItemFacing';
import { materialProgramInputs } from '../materialProgramInputs';
import { usePassMeshRef } from './lightPassDeclarations';
import { useDisposeOnRelease } from '../useDisposeOnRelease';
/**
 * Distinct stencil values in one pass. The buffer is 8-bit and 0 is the cleared state, so refs run
 * 1..255 and wrap: exact up to this many shadowed lights, and beyond it a pass needs a stencil
 * clear every 255 lights.
 */
const SHADOW_STENCIL_REFS = 255;
/**
 * The stencil value light `ordinal` stamps, distinct per light in a pass. The lights share one
 * target, so a shared ref would make each cookie reject every earlier light's shadow. A distinct
 * ref makes a leftover stamp unequal, so one stencil clear per pass suffices.
 */
function shadowStencilRef(ordinal) {
    const n = Math.abs(Math.trunc(ordinal)) % SHADOW_STENCIL_REFS;
    return n + 1;
}
/**
 * Render order for a light's volumes, just before its own quad. It takes the light's place in the
 * canvas light list (`lightSequence.ts`), not its ordinal: Godot applies lights in attach order,
 * MIX depends on it, and ordinals follow cookie load timing.
 */
function shadowVolumeRenderOrder(sequence) {
    return sequence * 2;
}
/** Render order for a light's cookie quad, just after its own volumes. */
export function litQuadRenderOrder(sequence) {
    return sequence * 2 + 1;
}
/**
 * Spread onto the light's cookie-quad material so it skips its own shadow.
 * Reads the stencil without touching it: the stamp stays until the next pass
 * clear, which is harmless because no other light tests this ref.
 */
export function litQuadStencilProps(ordinal) {
    return {
        stencilWrite: true,
        stencilRef: shadowStencilRef(ordinal),
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilZPass: THREE.KeepStencilOp,
    };
}
/**
 * Spread onto the `shadow_color` quad's material so it covers exactly the region
 * the lit quad skips. Same ref, `Equal` where the lit quad is `NotEqual`, so the
 * pair partitions the light's rect once, with no seam and no doubled pixel.
 */
export function shadowColorQuadStencilProps(ordinal) {
    return {
        ...litQuadStencilProps(ordinal),
        stencilFunc: THREE.EqualStencilFunc,
    };
}
/**
 * Written only by `scissorToRect`'s `onBeforeRender`, and read within that call, so every volume's
 * draw reuses them and a frame of lights allocates no scissor.
 */
const volumeScissor = new THREE.Vector4();
const targetSize = new THREE.Vector2();
/**
 * Draw hooks that scissor a light's volumes to its rect, within the target's own scissor. Only the
 * light's quads read the stencil, and they lie inside the rect, so the rest is fill a software
 * rasteriser pays for. A scissor, not a clip of the geometry: a clipped triangle has new endpoints,
 * and they move the shadow's edge pixels.
 */
function scissorToRect({ minX, minY, maxX, maxY }) {
    const rect = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    return {
        onBeforeRender(renderer, _scene, camera) {
            const target = renderer.getRenderTarget();
            if (!target)
                return;
            windowScissor(rect, camera, targetSize.set(target.width, target.height), volumeScissor);
            if (target.scissorTest)
                intersectScissor(volumeScissor, target.scissor);
            renderer.state.scissor(volumeScissor);
            renderer.state.setScissorTest(true);
        },
        onAfterRender(renderer) {
            const target = renderer.getRenderTarget();
            if (!target)
                return;
            renderer.state.scissor(target.scissor);
            renderer.state.setScissorTest(target.scissorTest);
        },
    };
}
export function ShadowVolumeMask({ light, casters, ordinal, sequence }) {
    const meshRef = usePassMeshRef(ordinal, 'volume');
    const positions = useMemo(() => buildShadowVolumes(light, casters), [light, casters]);
    const scissor = useMemo(() => scissorToRect(light.rect), [light.rect]);
    const geometry = useMemo(() => {
        if (!positions)
            return null;
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        return g;
    }, [positions]);
    // R3F does not auto-dispose a geometry handed over as a primitive.
    useDisposeOnRelease(geometry);
    if (!geometry)
        return null;
    const program = materialProgramInputs({
        props: {
            colorWrite: false,
            depthWrite: false,
            depthTest: false,
            // An additive cookie quad is transparent, and three draws the opaque group first, so an
            // opaque mask would stamp every light's volumes before any cookie. `renderOrder` interleaves
            // only within one group, and three's transparent sort puts it ahead of z.
            transparent: true,
            stencilWrite: true,
            stencilRef: shadowStencilRef(ordinal),
            stencilFunc: THREE.AlwaysStencilFunc,
            stencilFail: THREE.ReplaceStencilOp,
            stencilZFail: THREE.ReplaceStencilOp,
            stencilZPass: THREE.ReplaceStencilOp,
        },
        // A volume fans `[a, b, bFar, mFar, aFar]` (`shadowVolumes.ts`), so its winding follows its
        // edge's direction about the light, and `CULL_DISABLED` admits both. `ReplaceStencilOp` is
        // idempotent, so a doubled draw stamps the same value. An incrementing op would corrupt it.
        merge: [canvasItemFacing()],
    });
    return (_jsxs("mesh", { ref: meshRef, renderOrder: shadowVolumeRenderOrder(sequence), frustumCulled: false, 
        // `light` and `casters` are world coordinates, so the mesh stays at the identity, or the
        // CanvasItem chain applies twice. three's `updateMatrixWorld` recurses only into a child
        // that opts in, so nothing forces an update past this flag.
        matrixWorldAutoUpdate: false, onBeforeRender: scissor.onBeforeRender, onAfterRender: scissor.onAfterRender, children: [_jsx("primitive", { object: geometry, attach: "geometry" }), _jsx("meshBasicMaterial", { ...program.props }, program.key)] }));
}
//# sourceMappingURL=ShadowVolumeMask.js.map